import { apiErrorMessage } from '@agos/api-client'
import {
  getGetProductsQueryKey,
  useDeleteProductsProductId,
  useGetCategories,
  useGetProducts,
  useGetRecipes,
  usePatchProductsProductId,
} from '@agos/api-client/menu'
import type { Product, Recipe } from '@agos/api-client/model'
import { T, formatCount, formatMoney, tt } from '@agos/shared'
import {
  ButtonGroup,
  ConfirmDialog,
  DataTable,
  FilterBar,
  FilterSelect,
  PageHeader,
  cn,
  toast,
  type Column, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { ChefHat, Edit2, Package, Pizza, Plus, Trash2, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'

import { RecipeModal } from '../recipes/RecipeModal'
import { ProductDialog } from './ProductDialog'
import { useProductCosts } from './useProductCosts'

/**
 * Ported from legacy `pages/products.tsx`. Spec: `docs/pages/manager-menu/products.md`.
 *
 * The recipe is reached from the row and edited in `/recipes`' **own** modal — §9.13.
 *
 * This reverses what stood here: *the embedded recipe editor is deliberately not here — it duplicates
 * `/recipes`*. That reasoning was about a **copy**, and it was right about a copy. The owner asked to
 * see and edit a product's recipe from the product, which is a different thing: one editor, opened from
 * two places. `RecipeModal` is imported, not reimplemented, so there is still exactly one screen that
 * writes a recipe and one place a change to it has to be made.
 */

const PAGE_SIZE = 25

export function ProductsPage() {
  const queryClient = useQueryClient()
  const { data: products } = useGetProducts()
  const { data: categories } = useGetCategories()
  const { data: costs, isError: costsFailed } = useProductCosts()
  // §9.13. `productId` is the link a recipe already carries — `RecipeModal` has always written it, and
  // `/recipes` shows it as *Prepojený produkt*. Nothing new is stored to make this work.
  const { data: recipes, refetch: refetchRecipes } = useGetRecipes()

  const updateProduct = usePatchProductsProductId()
  const deleteProduct = useDeleteProductsProductId()

  const [recipeFor, setRecipeFor] = useState<Product | null>(null)

  /**
   * The recipe linked to a product, by `productId`.
   *
   * A `Map` rather than a `find` per row: `/products` pages 25 at a time but the table renders every
   * visible row on every keystroke of the search box, and a linear scan of the recipe list inside a
   * cell renderer is the shape that makes a filter feel slow on a till.
   *
   * **The first wins if two recipes name the same product.** Nothing stops that today — `productId` has
   * no unique constraint — and picking one is better than picking none. Which one is stable, because
   * the list arrives ordered.
   */
  const recipesByProduct = useMemo(() => {
    const byProduct = new Map<number, Recipe>()
    for (const recipe of recipes ?? []) {
      if (recipe.productId == null || byProduct.has(recipe.productId)) continue
      byProduct.set(recipe.productId, recipe)
    }
    return byProduct
  }, [recipes])

  const recipeOf = (product: Product): Recipe | undefined =>
    product.id == null ? undefined : recipesByProduct.get(product.id)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')
  const [editing, setEditing] = useState<Product | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [pendingDeletion, setPendingDeletion] = useState<Product | null>(null)

  const refreshProducts = () => queryClient.invalidateQueries({ queryKey: getGetProductsQueryKey() })

  const visibleProducts = useMemo(() => {
    const query = search.trim().toLowerCase()
    // Legacy guards with Array.isArray here: during a franchise switch the
    // query cache can briefly hold a non-array. The typed client makes that
    // unrepresentable, so the guard is the `?? []` below.
    return (products ?? []).filter((product) => {
      if (query && !product.name?.toLowerCase().includes(query)) return false
      if (categoryFilter && String(product.categoryId ?? '') !== categoryFilter) return false
      if (activeFilter === 'active' && !product.active) return false
      if (activeFilter === 'inactive' && product.active) return false
      return true
    })
  }, [products, search, categoryFilter, activeFilter])

  const categoryOptions = useMemo(
    () => [
      { value: '', label: tt('Všetky kategórie') },
      ...(categories ?? []).map((category) => ({
        value: String(category.id),
        label: category.name ?? '',
      })),
    ],
    [categories],
  )

  /**
   * §9.170 — one of the two switches beside *Stav*.
   *
   * `!== false` rather than `?? true` at every read, which is §9.155's rule: a payload from before
   * the field existed has no opinion and must not read as switched off.
   */
  const toggleField = (product: Product, field: 'visibleOnWeb' | 'orderable', next: boolean) => {
    if (product.id === undefined) return
    updateProduct.mutate(
      { productId: product.id, data: { [field]: next } },
      {
        onSuccess: () => {
          void refreshProducts()
          toast({
            title:
              field === 'visibleOnWeb'
                ? next
                  ? tt('Zobrazený na webe')
                  : tt('Skrytý na webe')
                : next
                  ? tt('Dostupný na objednanie')
                  : tt('Dočasne nedostupný'),
            description: product.name,
          })
        },
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Chyba'),
            description: apiErrorMessage(caught, tt('Nepodarilo sa zmeniť stav produktu.')),
          }),
      },
    )
  }

  const toggleActive = (product: Product) => {
    if (product.id === undefined) return
    const nextActive = !product.active

    updateProduct.mutate(
      { productId: product.id, data: { active: nextActive } },
      {
        onSuccess: () => {
          void refreshProducts()
          toast({
            title: nextActive ? tt('Produkt zapnutý') : tt('Produkt vypnutý'),
            description: product.name,
          })
        },
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Chyba'),
            description: apiErrorMessage(caught, tt('Nepodarilo sa zmeniť stav produktu.')),
          }),
      },
    )
  }

  /**
   * FB-033: an unknown cost is not a cost of zero. "We do not know what this
   * costs" and "this costs nothing" are different statements, and the second is
   * a reason to keep selling something at a loss.
   */
  const renderCost = (product: Product) => {
    const cost = product.id !== undefined ? costs?.get(product.id) : undefined
    if (!cost || cost.estimatedCost === null) {
      return (
        <span
          className="text-xs text-muted-foreground/50"
          title={tt('Pridajte recept a nákladovú cenu surovín pre výpočet')}
        >
          -
        </span>
      )
    }

    const isPartial = cost.ingredientsWithCost < cost.ingredientCount
    return (
      <div className="space-y-0.5">
        <div className="text-sm font-semibold text-red-400">{formatMoney(cost.estimatedCost)}</div>
        {cost.marginPercent !== null && (
          <div
            className={cn(
              'text-xs font-semibold',
              cost.marginPercent >= 60 ? 'text-emerald-500' : 'text-amber-500',
            )}
          >
            {Math.round(cost.marginPercent)}%
          </div>
        )}
        {isPartial && (
          // A margin from three costed ingredients out of eleven is not a
          // margin. Say so rather than let it read as one.
          <div
            className="text-[10px] text-amber-500"
            title={tt('Nie všetky suroviny majú nákladovú cenu')}
          >
            {cost.ingredientsWithCost}/{cost.ingredientCount}
          </div>
        )}
      </div>
    )
  }

  const renderPrice = (product: Product) => {
    if (product.productType === 'pizza' && product.pizzaSizes) {
      try {
        const sizes = JSON.parse(product.pizzaSizes) as Array<{
          label: string
          price?: string
          weightG?: string
        }>
        return (
          <div className="space-y-0.5">
            {sizes.map((size) => (
              <div key={size.label} className="flex items-center gap-2 text-xs">
                <span className="font-bold text-amber-600 w-10">{size.label}</span>
                <span className="font-bold text-foreground">
                  {size.price ? formatMoney(size.price) : '-'}
                </span>
                {size.weightG && <span className="text-muted-foreground">{size.weightG}g</span>}
              </div>
            ))}
          </div>
        )
      } catch {
        // A malformed blob falls back to the flat price, as legacy does.
        return <div className="font-bold text-foreground">{formatMoney(product.price)}</div>
      }
    }

    return (
      <div>
        <div className="font-bold text-foreground">{formatMoney(product.price)}</div>
        {(product.unitAmount || product.unit) && (
          <div className="text-xs text-muted-foreground">
            {product.unitAmount} {product.unit}
          </div>
        )}
      </div>
    )
  }

  const columns: Column<Product>[] = [
    {
      key: 'sortOrder',
      header: '#',
      headerClassName: 'w-12 text-center',
      className: 'text-center font-mono text-xs text-muted-foreground',
      sortable: true,
      sortValue: (row) => row.sortOrder ?? 0,
      render: (row) => row.sortOrder ?? 0,
    },
    {
      key: 'name',
      header: tt('Názov produktu'),
      sortable: true,
      sortValue: (row) => row.name ?? '',
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.imageUrl && (
            <img
              src={row.imageUrl}
              alt={row.name ?? ''}
              className="w-10 h-10 rounded-lg object-cover border border-border shrink-0"
            />
          )}
          {/* §9.269 - `min-w-0`, because an unclassed flex child computes `min-width: auto` and
            * cannot shrink below its content; and `flex-wrap`, so the *dynamická* badge drops under
            * a long name instead of shouldering it off a phone. Measured at 409 px in a 390 px
            * viewport, clipped rather than scrolled to. */}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 font-bold text-foreground">
              <T>{row.name}</T>
              {row.productType === 'pizza' && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-amber-500/15 text-amber-600 border border-amber-500/25 rounded px-1.5 py-0.5">
                  <Pizza className="w-2.5 h-2.5" /> pizza
                </span>
              )}
              {row.configPriced && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-violet-500/15 text-violet-600 border border-violet-500/25 rounded px-1.5 py-0.5">
                  <Zap className="w-2.5 h-2.5" /> <T>dynamická</T>
                </span>
              )}
            </div>
            {row.allergens && (
              <div className="text-xs text-amber-600 mt-0.5">
                <T>Alergény:</T> {row.allergens}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: tt('Kategória'),
      sortable: true,
      sortValue: (row) => row.categoryName ?? '',
      render: (row) => (
        <span className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold">
          {row.categoryName || tt('Nezaradené')}
        </span>
      ),
    },
    {
      key: 'price',
      header: tt('Cena / Meranie'),
      sortable: true,
      sortValue: (row) => row.price ?? 0,
      render: renderPrice,
    },
    {
      key: 'cost',
      header: tt('Náklad'),
      sortable: true,
      sortValue: (row) =>
        (row.id !== undefined ? costs?.get(row.id)?.estimatedCost : null) ?? -1,
      render: renderCost,
    },
    {
      // §9.191 — `/products` publishes all three of these and the table showed none, so the only
      // way to see what a product is taxed at was to open it.
      key: 'vat',
      header: tt('DPH'),
      headerTitle: tt(
        'Sadzba na mieste a pri rozvoze - §10A.3 dáva produktu inú sadzbu pre každý kanál. Ak má produkt kategóriu DPH, rozhoduje ona a sadzby na produkte sa nepoužijú.',
      ),
      headerClassName: 'text-right',
      className: 'text-right',
      sortable: true,
      sortValue: (row) => Number((row as { vatRateDineIn?: number }).vatRateDineIn ?? -1),
      render: (row) => {
        const entry = row as {
          vatRateDineIn?: number | null
          vatRateDelivery?: number | null
          vatCategoryId?: number | null
        }
        // A VAT *category* decides, and then the per-product rates are overridden — reading a stale
        // override as the truth is the drift this column exists to reveal.
        if (entry.vatCategoryId !== null && entry.vatCategoryId !== undefined) {
          return (
            <span className="text-xs text-muted-foreground" data-vat="category">
              <T>kategória</T>
            </span>
          )
        }
        const dineIn = entry.vatRateDineIn
        const delivery = entry.vatRateDelivery
        if (dineIn == null && delivery == null) {
          return (
            <span className="text-xs text-muted-foreground" data-vat="none">
              -
            </span>
          )
        }
        return (
          <span className="text-xs tabular-nums whitespace-nowrap" data-vat="rates">
            {dineIn == null ? '-' : `${formatCount(dineIn)} %`}
            <span className="text-muted-foreground"> / </span>
            {delivery == null ? '-' : `${formatCount(delivery)} %`}
          </span>
        )
      },
    },
    {
      key: 'state',
      header: tt('Stav'),
      headerClassName: 'text-center',
      className: 'text-center',
      sortable: true,
      sortValue: (row) => (row.active ? 1 : 0),
      render: (row) => (
        <RowSwitch
          on={Boolean(row.active)}
          label={tt('Aktívny')}
          hook="active"
          onToggle={() => toggleActive(row)}
        />
      ),
    },
    // §9.170 — the other two switches a product has carried since §9.155. They are **not** a ladder:
    // a product can be active, invisible and orderable at once, so showing only *Stav* made one
    // missing from the customer site look switched on with no way to see why but to open it.
    {
      key: 'visible',
      header: tt('Viditeľný na webe'),
      headerClassName: 'text-center',
      className: 'text-center',
      sortable: true,
      sortValue: (row) => (row.visibleOnWeb === false ? 0 : 1),
      render: (row) => (
        <RowSwitch
          on={row.visibleOnWeb !== false}
          label={tt('Viditeľný na webe')}
          hook="visible"
          onToggle={() => toggleField(row, 'visibleOnWeb', row.visibleOnWeb === false)}
        />
      ),
    },
    {
      key: 'orderable',
      // §9.155's own wording. A header invented for the table would be a fourth name for a switch
      // that already has one, and the dialog's explanation is what tells anybody what it means.
      header: tt('Dostupné'),
      headerClassName: 'text-center',
      className: 'text-center',
      sortable: true,
      sortValue: (row) => (row.orderable === false ? 0 : 1),
      render: (row) => (
        <RowSwitch
          on={row.orderable !== false}
          label={tt('Dostupný na objednanie')}
          hook="orderable"
          onToggle={() => toggleField(row, 'orderable', row.orderable === false)}
        />
      ),
    },
    {
      key: 'actions',
      header: tt('Akcie'),
      headerClassName: 'text-right',
      className: 'text-right',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          {/* §9.13. One button for both cases: the product's recipe if it has one, a new recipe already
            * linked to it if it does not. Two buttons would ask the user to know which, and the answer
            * is on the screen already — the amber tint. */}
          <button
            type="button"
            aria-label={recipeOf(row) ? tt('Upraviť recept') : tt('Vytvoriť recept')}
            title={recipeOf(row)?.name ?? tt('Vytvoriť recept')}
            data-product-recipe={row.id}
            data-has-recipe={recipeOf(row) !== undefined}
            onClick={(event) => {
              event.stopPropagation()
              setRecipeFor(row)
            }}
            className={cn(
              'p-2 rounded-lg transition-colors',
              recipeOf(row)
                ? 'text-amber-500 hover:bg-amber-500/10'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            <ChefHat className="w-4 h-4" />
          </button>
          <button
            type="button"
            // *Upraviť produkt*, not *Upraviť*. §9.13 put a second edit in this row — the recipe —
            // and one row offering two edits, one of them named only *Upraviť*, is ambiguous to a
            // screen reader before it is ambiguous to a test.
            aria-label={tt('Upraviť produkt')}
            onClick={(event) => {
              event.stopPropagation()
              setEditing(row)
            }}
            className="p-2 text-blue-500 hover:bg-blue-500/10 rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label={tt('Zmazať')}
            onClick={(event) => {
              event.stopPropagation()
              setPendingDeletion(row)
            }}
            className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={tt('Produkty')}
        description={`${visibleProducts.length} ${tt('produktov')}`}
        icon={<Package className="w-5 h-5" />}
        actions={
          <button
            type="button"
            onClick={() => setIsCreating(true)}
            // §9.33 — the house style every other page-header action uses. This one carried the
            // colour and none of the shape: no padding, no corner radius, no text size, so it drew
            // as a hard-edged block of primary with the label against its edges.
            className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            <Plus className="w-4 h-4" />
            <T>Pridať produkt</T>
          </button>
        }
      />

      {/* FB-032: legacy swallows this failure, so a menu whose costs simply did
          not load is indistinguishable from one nobody has costed. */}
      {costsFailed && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 text-xs text-amber-600">
          <T>Nákladové ceny sa nepodarilo načítať - stĺpec Náklad je prázdny, nie nulový.</T>
        </div>
      )}

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať produkt...') }}
        filters={
          <>
            <FilterSelect
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={categoryOptions}
            />
            <ButtonGroup
              value={activeFilter}
              onChange={setActiveFilter}
              options={[
                { value: 'all', label: tt('Všetky') },
                { value: 'active', label: tt('Aktívne') },
                { value: 'inactive', label: tt('Neaktívne') },
              ]}
            />
          </>
        }
      />

      <DataTable
        columns={columns}
        data={visibleProducts}
        rowKey={(row) => row.id ?? row.name ?? ''}
        pageSize={PAGE_SIZE}
        emptyMessage={tt('Žiadne produkty nenájdené.')}
      />

      {(isCreating || editing) && (
        <ProductDialog
          product={editing}
          products={products ?? []}
          categories={categories ?? []}
          onClose={() => {
            setIsCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setIsCreating(false)
            setEditing(null)
            void refreshProducts()
          }}
        />
      )}

      {/* §9.13 - `/recipes`' own modal, imported rather than reimplemented. With no recipe yet it opens
        * on a draft already linked to the product and named after it, so the two fields somebody would
        * otherwise have to fill correctly for the link to work are filled. `RecipeModal` decides create
        * against update on `recipe?.id`, so a draft without one creates. */}
      {recipeFor !== null && (
        <RecipeModal
          recipe={recipeOf(recipeFor) ?? { name: recipeFor.name, productId: recipeFor.id }}
          onClose={() => setRecipeFor(null)}
          onSaved={() => {
            setRecipeFor(null)
            void refetchRecipes()
            // The cost column is computed from the recipe, so it is stale the moment one is saved.
            void queryClient.invalidateQueries({ queryKey: ['products', 'costs'] })
          }}
        />
      )}

      <ConfirmDialog
        open={pendingDeletion !== null}
        onClose={() => setPendingDeletion(null)}
        onConfirm={() => {
          const productId = pendingDeletion?.id
          if (productId === undefined) return
          deleteProduct.mutate(
            { productId },
            {
              onSuccess: () => {
                void refreshProducts()
                setPendingDeletion(null)
                toast({ title: tt('Vymazané'), description: tt('Produkt bol odstránený.') })
              },
            },
          )
        }}
        loading={deleteProduct.isPending}
        variant="danger"
        title={tt('Vymazať produkt?')}
        description={`${tt('Produkt')} "${pendingDeletion?.name ?? ''}" ${tt('sa odstráni z menu aj z kasy.')}`}
        confirmLabel={tt('Vymazať')}
      />
    </div>
  )
}

/**
 * One switch in a table row — §9.170.
 *
 * Extracted when the second and third arrived: three copies of the same fourteen classes is how
 * §9.3's fourteen different primary buttons happened. `stopPropagation` because the row itself opens
 * the editor, and toggling a switch must not do both.
 */
function RowSwitch(props: { on: boolean; label: string; hook: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      data-row-switch={props.hook}
      aria-checked={props.on}
      aria-label={props.label}
      title={props.label}
      onClick={(event) => {
        event.stopPropagation()
        props.onToggle()
      }}
      className={cn(
        'w-10 h-5 rounded-full transition-all relative',
        props.on ? 'bg-primary' : 'bg-muted',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all',
          props.on ? 'left-5' : 'left-0.5',
        )}
      />
    </button>
  )
}
